/**
 * Songs Store - Manages songs and voting state
 *
 * Handles song fetching, submission, and real-time vote updates.
 */

import { signal, computed, effect } from '@preact/signals';
import type { Song, VoteType, SongSubmission, SampleSong } from '@/types';
import { api } from '@/api/client';
import { isAuthenticated, user } from './auth';
import { selectedGroupId } from './groups';
import { broadcastVote, isConnected } from './websocket';

// ============================================
// STATE
// ============================================

export const songs = signal<Song[]>([]);
export const sampleSongs = signal<SampleSong[]>([]);
export const isLoading = signal(false);
export const isSubmitting = signal(false);
export const error = signal<string | null>(null);

// Search state
export const searchQuery = signal('');
export const searchResults = signal<Song[]>([]);
export const isSearching = signal(false);

// Current playing song for player voting
export const currentSong = signal<Song | null>(null);

// User's vote on current song
export const userVote = computed(() => {
  return currentSong.value?.userVote || null;
});

// Set the current song being played
export function setCurrentSong(song: Song | null): void {
  currentSong.value = song;
}

// ============================================
// COMPUTED
// ============================================

export const sortedSongs = computed(() => {
  return [...songs.value].sort((a, b) => b.votes.score - a.votes.score);
});

export const userSubmission = computed(() => {
  if (!user.value) return null;
  return songs.value.find(s => s.userId === user.value!.id) || null;
});

export const hasSubmitted = computed(() => !!userSubmission.value);

export const songsToVote = computed(() => {
  if (!user.value) return [];
  return songs.value.filter(s => s.userId !== user.value!.id && s.userVote === null);
});

export const votingProgress = computed(() => {
  const total = songs.value.filter(s => s.userId !== user.value?.id).length;
  const voted = total - songsToVote.value.length;
  return { voted, total, percentage: total > 0 ? (voted / total) * 100 : 0 };
});

// ============================================
// ACTIONS
// ============================================

/**
 * Fetch songs for selected group
 */
export async function fetchSongs(): Promise<void> {
  if (!selectedGroupId.value) {
    songs.value = [];
    return;
  }

  // If not authenticated, fetch sample songs instead
  if (!isAuthenticated.value) {
    await fetchSampleSongs();
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get<{ songs: Song[] }>(
      `/api/songs/group/${selectedGroupId.value}`
    );

    if (response.data?.songs) {
      songs.value = response.data.songs;
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch songs';
    console.error('Failed to fetch songs:', e);
  } finally {
    isLoading.value = false;
  }
}

/**
 * Fetch sample songs for unauthenticated users
 * Uses hardcoded data - no API call needed
 */
export async function fetchSampleSongs(): Promise<void> {
  isLoading.value = true;

  try {
    // Import hardcoded sample songs
    const { sampleSongs: hardcodedSongs } = await import('@/data/sampleSongs');
    sampleSongs.value = hardcodedSongs;
  } catch (e) {
    console.error('Failed to load sample songs:', e);
  } finally {
    isLoading.value = false;
  }
}

/**
 * Submit a song
 */
export async function submitSong(submission: SongSubmission): Promise<Song | null> {
  if (!isAuthenticated.value || !selectedGroupId.value) return null;

  isSubmitting.value = true;
  error.value = null;

  try {
    const response = await api.post<{ song: Song }>('/api/songs/submit', {
      ...submission,
      groupId: selectedGroupId.value,
    });

    if (response.data?.song) {
      songs.value = [...songs.value, response.data.song];
      return response.data.song;
    }

    error.value = response.error || 'Failed to submit song';
    return null;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to submit song';
    return null;
  } finally {
    isSubmitting.value = false;
  }
}

/**
 * Delete a song submission
 */
export async function deleteSong(songId: string): Promise<boolean> {
  if (!isAuthenticated.value) return false;

  try {
    const response = await api.delete(`/api/songs/${songId}`);

    if (!response.error) {
      songs.value = songs.value.filter(s => s.id !== songId);
      return true;
    }

    return false;
  } catch (e) {
    console.error('Failed to delete song:', e);
    return false;
  }
}

/**
 * Vote on a song
 */
export async function vote(songId: string, voteType: VoteType): Promise<boolean> {
  if (!isAuthenticated.value || !selectedGroupId.value) return false;

  // Optimistic update
  const previousSongs = songs.value;
  songs.value = songs.value.map(s => {
    if (s.id !== songId) return s;

    // Calculate new vote counts
    const oldVote = s.userVote;
    const newVotes = { ...s.votes };

    // Remove old vote
    if (oldVote) {
      newVotes[oldVote]--;
    }

    // Add new vote
    newVotes[voteType]++;

    // Recalculate score: like=2, neutral=1, dislike=0
    newVotes.score = newVotes.like * 2 + newVotes.neutral * 1;
    newVotes.total = newVotes.like + newVotes.neutral + newVotes.dislike;

    return {
      ...s,
      votes: newVotes,
      userVote: voteType,
    };
  });

  try {
    const response = await api.post('/api/songs/vote', {
      songId,
      groupId: selectedGroupId.value,
      voteType,
    });

    if (response.error) {
      // Revert optimistic update
      songs.value = previousSongs;
      return false;
    }

    // Broadcast to other users via WebSocket
    if (isConnected.value) {
      broadcastVote(songId, voteType);
    }

    return true;
  } catch (e) {
    // Revert optimistic update
    songs.value = previousSongs;
    console.error('Failed to vote:', e);
    return false;
  }
}

/**
 * Update song from WebSocket broadcast
 */
export function updateSongVotes(
  songId: string,
  votes: { like: number; dislike: number; neutral: number; score: number }
): void {
  songs.value = songs.value.map(s => {
    if (s.id !== songId) return s;
    return {
      ...s,
      votes: {
        ...votes,
        total: votes.like + votes.neutral + votes.dislike,
      },
    };
  });
}

// ============================================
// EFFECTS
// ============================================

// Refetch songs when group changes
effect(() => {
  const groupId = selectedGroupId.value;
  if (groupId) {
    fetchSongs();
  }
});
